/**
 * Scalinata-specific event helpers.
 * 
 * Thin wrapper around the generic dispatchEvent function for Scalinata game events.
 * Scalinata has five event types:
 * - 'submit': Submit a word guess for the current level
 * - 'hint_definition': Get a dictionary definition for the current word
 * - 'hint_clue': Get a clue/riddle for the current word
 * - 'hint_magic': Reveal 3 random correct letters
 * - 'hint_reveal_theme': Reveal the theme connecting all words (one-time use)
 * 
 * Usage:
 * ```javascript
 * const { dispatchEvent, dispatchHintEvent } = useGameplaySession('scalinata', date);
 * const { 
 *   submitWord, 
 *   requestDefinition, 
 *   requestClue, 
 *   requestMagic, 
 *   requestRevealTheme 
 * } = useScalinataEvents(dispatchEvent, dispatchHintEvent);
 * 
 * const result = await submitWord(0, 'ARTE');
 * if (result.success) {
 *   // result.result contains: { valid, feedback?, level_complete, game_won, game_lost, answers? }
 * }
 * ```
 */

import { useCallback } from 'react';

/**
 * Hook providing Scalinata-specific event helpers.
 * 
 * @param {function} dispatchEvent - The dispatchEvent function from useGameplaySession
 * @param {function} dispatchHintEvent - The dispatchHintEvent function from useGameplaySession
 * @returns {object} Scalinata event helpers
 * @returns {function} returns.submitWord - Submit a word guess for the current level
 * @returns {function} returns.requestDefinition - Request a dictionary definition hint
 * @returns {function} returns.requestClue - Request a clue/riddle hint
 * @returns {function} returns.requestMagic - Request magic letters hint
 * @returns {function} returns.requestRevealTheme - Request to reveal the theme
 */
export function useScalinataEvents(dispatchEvent, dispatchHintEvent) {
  /**
   * Submit a word guess for the current level.
   * 
   * @param {number} level - Current level (0-4)
   * @param {string} word - The guessed word (must match level's expected length: level + 4)
   * @returns {Promise<{
   *   success: boolean,
   *   result?: {
   *     valid: boolean,
   *     reason?: 'NOT_IN_DICTIONARY',
   *     feedback?: Array<{letter: string, state: 'correct' | 'position' | 'incorrect'}>,
   *     level_complete: boolean,
   *     game_won: boolean,
   *     game_lost: boolean,
   *     answers?: string[]
   *   },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await submitWord(0, 'ARTE');
   * if (result.success) {
   *   if (!result.result.valid) {
   *     // Word not in dictionary - no guess consumed
   *   } else if (result.result.level_complete) {
   *     // Level completed! Check result.result.game_won
   *   } else {
   *     // Wrong guess, check result.result.feedback
   *   }
   * }
   */
  const submitWord = useCallback(async (level, word) => {
    return dispatchEvent('submit', { level, word });
  }, [dispatchEvent]);

  /**
   * Request a dictionary definition for the current level's word.
   * Requires authentication and sufficient gettoncini balance.
   * This hint is reusable (charged each time).
   * 
   * @param {number} level - Current level (0-4)
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { definition: string },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await requestDefinition(1, () => showSignupModal());
   * if (result.success) {
   *   const definition = result.hint_data.definition;
   *   // Display definition to user
   * }
   */
  const requestDefinition = useCallback(async (level, onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_definition', { level }, onNotAuthenticated);
  }, [dispatchHintEvent]);

  /**
   * Request a clue/riddle for the current level's word.
   * Requires authentication and sufficient gettoncini balance.
   * This hint is reusable (charged each time).
   * 
   * @param {number} level - Current level (0-4)
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { clue: string },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await requestClue(1, () => showSignupModal());
   * if (result.success) {
   *   const clue = result.hint_data.clue;
   *   // Display clue to user
   * }
   */
  const requestClue = useCallback(async (level, onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_clue', { level }, onNotAuthenticated);
  }, [dispatchHintEvent]);

  /**
   * Request to reveal 3 random letters in their correct positions.
   * Requires authentication and sufficient gettoncini balance.
   * This hint is reusable (charged each time). Magic letters are consumed after the next guess.
   * 
   * @param {number} level - Current level (0-4)
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { magic_letters: Array<string|null> },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await requestMagic(1, () => showSignupModal());
   * if (result.success) {
   *   const magicLetters = result.hint_data.magic_letters;
   *   // e.g., ["P", null, "S", null, "A"] - letters at positions 0, 2, 4
   * }
   */
  const requestMagic = useCallback(async (level, onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_magic', { level }, onNotAuthenticated);
  }, [dispatchHintEvent]);

  /**
   * Request to reveal the theme connecting all 5 words.
   * Requires authentication and sufficient gettoncini balance.
   * This hint can only be used once per game.
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { theme: string },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await requestRevealTheme(() => showSignupModal());
   * if (result.success) {
   *   const theme = result.hint_data.theme;
   *   // Display theme above the game board
   * }
   */
  const requestRevealTheme = useCallback(async (onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_reveal_theme', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  return {
    submitWord,
    requestDefinition,
    requestClue,
    requestMagic,
    requestRevealTheme,
  };
}

